import find from 'lodash/find';
import React, { FC, useCallback } from 'react';
import styled from 'styled-components';

import ConditionRow from './ConditionRow';
import { ConditionOperator, SkillCondition as SkillConditionType } from 'src/actions/rules/condition';
import skillsResource from 'src/actions/skills/skillsResource';
import {
  StyledDropdown,
  StyledInformationIcon,
  StyledOperatorDropdown,
  StyledOperatorLabel,
  StyledTooltip,
} from 'src/presentation/components/pages/rules/shared/StyledComponents';
import { useDropdownOptions } from 'src/presentation/components/pages/skills/hierarchy';
import { useResourceValues } from 'src/presentation/hooks/useResource';

type SkillConditionProps = {
  condition: SkillConditionType;
  index: number;
  isReadOnly?: boolean;
  onDelete: Function;
  onUpdate: Function;
};

const SkillCondition: FC<SkillConditionProps> = ({ condition, index, isReadOnly, onDelete, onUpdate }) => {
  const onOperatorChange = useCallback(
    val => {
      condition.setOperator(val);
      onUpdate({ condition, index });
    },
    [condition, index, onUpdate]
  );

  const onSkillChange = useCallback(
    skillId => {
      condition.updateSkill(skillId);
      onUpdate({ condition, index });
    },
    [condition, index, onUpdate]
  );

  const { values: skills } = useResourceValues(skillsResource);

  const skillOptions: { label: string | undefined; value: string }[] = [];
  useDropdownOptions(skills).forEach(skill => {
    if (!skill.disabled) {
      skillOptions.push({ label: skill.label, value: skill.value });
    }
  });

  return (
    <ConditionRow index={index} isReadOnly={isReadOnly} onDelete={onDelete} text="Skills include">
      {isReadOnly ? renderReadOnlyCondition() : renderEditableCondition()}
    </ConditionRow>
  );

  function renderEditableCondition() {
    return (
      <>
        <StyledOperatorDropdown
          data-aid="skillConditionOperatorDropdown"
          onSelect={onOperatorChange}
          options={operatorOptions}
          value={condition.operator}
        />
        <StyledDropdown
          data-aid="skillConditionValuesDropdown"
          isMultiSelect
          leftMargin
          maxRows={5}
          multiSelectLabel="skills"
          onSelect={onSkillChange}
          options={skillOptions}
          placeholder="Select skills"
          searchable
          value={condition.value}
        />
        <StyledTooltip
          data-aid="skillConditionTooltip"
          message="Priority Boosts with a Skills condition should also include a customer condition"
        >
          <StyledSkillConditionInfoIcon />
        </StyledTooltip>
      </>
    );
  }

  function renderReadOnlyCondition() {
    return (
      <>
        <StyledOperatorLabel data-aid="readOnlySkillConditionOperator">
          {renderOperator(condition.operator)}
        </StyledOperatorLabel>
        <span data-aid="readOnlySkillConditionValues">{renderSkills(condition.value, skillOptions)}</span>
      </>
    );
  }
};

function renderOperator(value: string) {
  const operator = find(operatorOptions, { value });
  return operator ? operator.label : '';
}

function renderSkills(skills, skillOptions) {
  return skills.map(skill => find(skillOptions, { value: skill }).label).join(', ');
}

const StyledSkillConditionInfoIcon = styled(StyledInformationIcon)`
  height: 18px;
  padding-left: ${p => p.theme.spacing.small};
  width: 18px;
`;

const operatorOptions: { label: string; value: string }[] = [
  { label: 'All of', value: ConditionOperator.And },
  { label: 'Any of', value: ConditionOperator.Or },
];

export default SkillCondition;
